import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/admin/StatusBadge';
import { fmtDate, fmtDay, toIsoDay } from '@/lib/format';
import { TID } from '@/lib/testIds';
import { toast } from 'sonner';
import { X, Phone, Mail } from 'lucide-react';

const STATUSES = ['pending', 'confirmed', 'seated', 'completed', 'cancelled', 'no_show'];
const NEXT_BY_STATUS = { pending: 'confirmed', confirmed: 'seated', seated: 'completed' };

export default function AdminReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [date, setDate] = useState('');
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    const { data } = await api.get(`/admin/reservations${params.toString() ? '?' + params.toString() : ''}`);
    setReservations(data || []);
  };
  useEffect(() => { load(); }, [date]);

  const open = async (r) => {
    setActive(r);
    const { data } = await api.get(`/admin/reservations/${r.id}`);
    setHistory(data.history || []);
  };

  const updateStatus = async (status) => {
    if (!active) return;
    try {
      await api.put(`/admin/reservations/${active.id}/status`, { status });
      toast.success(`Reservation moved to ${status}`);
      await load();
      const { data } = await api.get(`/admin/reservations/${active.id}`);
      setActive(data.reservation); setHistory(data.history || []);
    } catch (e) { toast.error('Could not update status'); }
  };

  const filtered = filter === 'all' ? reservations : reservations.filter((r) => r.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: reservations.filter((r) => r.status === s).length }), {});

  return (
    <AdminLayout>
      <div data-testid={TID.admin.reservationsPage}>
        <h1 className="font-display text-3xl text-white">RESERVATIONS</h1>
        <p className="text-sm text-white/65 mb-5">All upcoming and past table bookings.</p>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <button onClick={() => setFilter('all')} className={`h-9 px-4 rounded-full border text-sm ${filter === 'all' ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]' : 'bg-[hsl(var(--sb-surface))] text-white/85 border-[hsl(var(--sb-border))]'}`}>All ({reservations.length})</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`h-9 px-4 rounded-full border text-sm capitalize ${filter === s ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]' : 'bg-[hsl(var(--sb-surface))] text-white/85 border-[hsl(var(--sb-border))]'}`}>{s.replace('_', ' ')} ({counts[s] || 0})</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white text-sm" />
            {date && <button onClick={() => setDate('')} className="text-xs text-white/60">Clear</button>}
          </div>
        </div>

        <div className="sb-card overflow-x-auto">
          <table data-testid={TID.admin.dataTable} className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-white/55 bg-black/30">
              <tr>
                <th className="text-left px-4 py-3">Reference</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Time</th>
                <th className="text-left px-4 py-3">Guests</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => open(r)} className="border-t border-[hsl(var(--sb-border))] hover:bg-white/5 cursor-pointer">
                  <td className="px-4 py-3 font-display text-base text-[hsl(var(--sb-gold))]">{r.reference}</td>
                  <td className="px-4 py-3 text-white">{r.customer?.name}<div className="text-[11px] text-white/55">{r.customer?.phone}</div></td>
                  <td className="px-4 py-3 text-white">{fmtDay(r.date)}</td>
                  <td className="px-4 py-3 text-white">{r.time}</td>
                  <td className="px-4 py-3 text-white">{r.party_size}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={6} className="py-10 text-center text-white/55">No reservations.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setActive(null)}>
          <div data-testid={TID.admin.detailDrawer} className="w-full max-w-lg bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-start justify-between">
              <div>
                <div className="font-display text-2xl text-[hsl(var(--sb-gold))]">{active.reference}</div>
                <div className="text-xs text-white/60 mt-1">{fmtDay(active.date)} · {active.time} · {active.party_size} guests</div>
                <div className="mt-2"><StatusBadge status={active.status} /></div>
              </div>
              <button onClick={() => setActive(null)} className="h-9 w-9 grid place-items-center text-white/70"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-5">
              <section>
                <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2">Customer</div>
                <div className="text-sm text-white">{active.customer?.name}</div>
                <div className="text-xs text-white/70 flex items-center gap-2 mt-1"><Phone size={12} />{active.customer?.phone}</div>
                <div className="text-xs text-white/70 flex items-center gap-2 mt-1"><Mail size={12} />{active.customer?.email}</div>
              </section>
              {active.special_requests && (
                <section className="bg-[hsl(var(--sb-gold))]/10 border border-[hsl(var(--sb-gold))]/30 rounded-md p-3 text-sm text-[hsl(var(--sb-gold))]">
                  <strong className="block text-[11px] uppercase tracking-widest mb-1">Special requests</strong>
                  {active.special_requests}
                </section>
              )}
              <section>
                <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {NEXT_BY_STATUS[active.status] && (
                    <button onClick={() => updateStatus(NEXT_BY_STATUS[active.status])} className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold capitalize">Move to {NEXT_BY_STATUS[active.status]}</button>
                  )}
                  {STATUSES.filter((s) => s !== active.status).map((s) => (
                    <button key={s} onClick={() => updateStatus(s)} className="h-10 px-3 rounded-md border border-[hsl(var(--sb-border))] text-white/85 capitalize hover:border-white/40">{s.replace('_', ' ')}</button>
                  ))}
                </div>
              </section>
              <section>
                <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2">History</div>
                <ul className="space-y-2 text-xs text-white/70">
                  {history.map((h) => (
                    <li key={h.id} className="flex justify-between gap-3">
                      <span>{h.from_status || '—'} → <span className="text-white">{h.to_status}</span></span>
                      <span>{fmtDate(h.changed_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
